import React, { FC } from 'react';
import { Card } from 'antd';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	renderCurrencyByCurrencyId,
} from '../../utils/DataAccessUtils';
import { dateFormatterWithoutTimeZone } from '../../utils/DataFormatterUtils';
import { UserName } from '../name_component/user/UserNameComponent';
import SpotPurchaseItemsCell from '../supplier_work_orders_detail_charges_page/SpotPurchaseItemsCell';
import {
	getSpotPurchaseAggregate,
	getSpotPurchaseDisplayCurrencyId,
	getSpotPurchaseReceiptUrl,
} from '../../utils/SpotPurchaseUtils';

interface WorkOrderSpotPurchasesPrintSectionProps {
	spotPurchases: any[];
	showCost: boolean;
	currentUser: any;
	userType: string;
}

const renderPurchasedBy = (spotPurchase: any, userType: string) => {
	const purchasedBy =
		nullSafeGet('purchasedByContact', spotPurchase) || nullSafeGet('purchasedBy', spotPurchase);
	const email = nullSafeGet('purchasedByEmail', spotPurchase) || nullSafeGet('email', purchasedBy);
	// The tab falls back to an avatar-only EmailUserName; a printout has no hover, so spell
	// the email out. UserName stays avatar-less here for the same reason the parts section
	// drops its thumbnails — avatars paint via CSS background-image, which print omits.
	if (purchasedBy) {
		return (
			<UserName contact={purchasedBy} userType={userType as any} mode="inline" underline={false} />
		);
	}
	return email ? <span>{email}</span> : <span>—</span>;
};

export const WorkOrderSpotPurchasesPrintSection: FC<WorkOrderSpotPurchasesPrintSectionProps> = ({
	spotPurchases,
	showCost,
	currentUser,
	userType,
}): React.ReactElement => {
	const displayCurrencyId = getSpotPurchaseDisplayCurrencyId(userType, currentUser);
	// null when the work order's purchases span more than one currency — see
	// getSpotPurchaseAggregate. The PDF drops the total rather than printing a sum that is in no
	// currency at all, which accounting would have no way to spot.
	const aggregate = getSpotPurchaseAggregate(spotPurchases, displayCurrencyId);

	return (
		<div className="mt-4">
			<Card
				title="Spot Purchases"
				extra={
					showCost && aggregate ? (
						<div>{`Total Cost: ${renderCurrencyByCurrencyId(aggregate.currencyId)(
							aggregate.totalCost
						)}`}</div>
					) : null
				}
			>
				<table className="w-full text-left">
					<thead>
						<tr className="border-0 border-b border-solid border-gray-300">
							<th className="bodymedium w-48 py-2">Purchased by</th>
							<th className="bodymedium w-32 py-2">Added On</th>
							<th className="bodymedium py-2">Items</th>
							{showCost ? <th className="bodymedium w-32 py-2 text-right">Total</th> : null}
						</tr>
					</thead>
					<tbody>
						{spotPurchases.map((spotPurchase) => {
							const purchasedAt =
								nullSafeGet('purchasedAt', spotPurchase) || nullSafeGet('createdAt', spotPurchase);
							const recordCurrencyId = nullSafeGetOrElse(
								'currencyId',
								spotPurchase,
								displayCurrencyId
							);
							const receiptViewUrl = getSpotPurchaseReceiptUrl(userType, spotPurchase);
							return (
								<tr
									key={spotPurchase.id}
									className="border-0 border-b border-solid border-gray-200"
								>
									<td className="bodyregular py-2 pr-4 align-top">
										{renderPurchasedBy(spotPurchase, userType)}
									</td>
									<td className="bodyregular py-2 pr-4 align-top">
										{purchasedAt ? dateFormatterWithoutTimeZone(purchasedAt, 'D MMM, YYYY') : '—'}
									</td>
									<td className="py-2 pr-4 align-top">
										<SpotPurchaseItemsCell
											items={nullSafeGetOrElse('items', spotPurchase, [])}
											currencyId={recordCurrencyId}
											taxAmount={nullSafeGet('taxAmount', spotPurchase)}
											showCost={showCost}
											isPrintView
										/>
									</td>
									{showCost ? (
										<td className="bodyregular whitespace-nowrap py-2 text-right align-top">
											{/* The amount was stored in the recording user's currency, so format it
											    in that currency — recordCurrencyId already backs the item lines. */}
											<div>
												{renderCurrencyByCurrencyId(recordCurrencyId)(
													nullSafeGetOrElse('totalCost', spotPurchase, 0)
												)}
											</div>
											{receiptViewUrl ? (
												<a href={receiptViewUrl} target="_blank" rel="noopener noreferrer">
													view receipt
												</a>
											) : null}
										</td>
									) : null}
								</tr>
							);
						})}
					</tbody>
				</table>
			</Card>
		</div>
	);
};

export default WorkOrderSpotPurchasesPrintSection;
